import { sortBy } from 'lodash';
import { RhombusCameraSnapshotStatus, SnapshotError } from '../../../types';

// Canvas requires hex values; colors match the UI status classes (bg-success, bg-warning, bg-danger).
const SNAPSHOT_STATUS_COLORS: Partial<Record<RhombusCameraSnapshotStatus, string>> = {
  [RhombusCameraSnapshotStatus.Healthy]: '#059669',
  [RhombusCameraSnapshotStatus.Warning]: '#d97706',
  [RhombusCameraSnapshotStatus.Unhealthy]: '#dc2626',
};

const STATUS_ATTENTION_ORDER: Record<RhombusCameraSnapshotStatus, number> = {
  [RhombusCameraSnapshotStatus.Unhealthy]: 0,
  [RhombusCameraSnapshotStatus.Warning]: 1,
  [RhombusCameraSnapshotStatus.Healthy]: 2,
  [RhombusCameraSnapshotStatus.Unassigned]: 3,
};

function canvasToBlob(canvas: HTMLCanvasElement, fallbackBlob: Blob): Promise<Blob> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? fallbackBlob), 'image/jpeg', 0.92);
  });
}

function drawImageLabel(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  cameraName: string,
  locationName: string,
) {
  const fontSize = Math.max(18, Math.min(34, Math.round(canvasWidth * 0.04)));
  const lineHeight = fontSize * 1.15;
  const y = canvasHeight - Math.max(28, canvasHeight * 0.08);

  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
  ctx.shadowBlur = 5;

  ctx.font = `700 ${fontSize}px Arial, sans-serif`;
  ctx.fillText(locationName, canvasWidth / 2, y - lineHeight, canvasWidth - 32);
  ctx.font = `400 ${Math.round(fontSize * 0.9)}px Arial, sans-serif`;
  ctx.fillText(cameraName, canvasWidth / 2, y, canvasWidth - 32);

  ctx.restore();
}

function drawStatusBadge(ctx: CanvasRenderingContext2D, canvasWidth: number, status: RhombusCameraSnapshotStatus) {
  if (status === RhombusCameraSnapshotStatus.Unassigned) return;

  const dotColor = SNAPSHOT_STATUS_COLORS[status];
  if (!dotColor) return;

  const fontSize = Math.max(12, Math.min(18, Math.round(canvasWidth * 0.022)));
  const offset = Math.max(2, Math.round(canvasWidth * 0.004));
  const padding = fontSize * 0.75;
  const dotSize = fontSize * 0.6;
  const gap = fontSize * 0.45;

  ctx.save();
  ctx.font = `700 ${fontSize}px Arial, sans-serif`;
  ctx.textBaseline = 'middle';

  const textWidth = ctx.measureText(status).width;
  const badgeWidth = padding * 2 + dotSize + gap + textWidth;
  const badgeHeight = fontSize + padding;
  const x = canvasWidth - offset - badgeWidth;
  const y = offset;

  ctx.beginPath();
  ctx.roundRect(x, y, badgeWidth, badgeHeight, badgeHeight * 0.25);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x + padding + dotSize / 2, y + badgeHeight / 2, dotSize / 2, 0, Math.PI * 2);
  ctx.fillStyle = dotColor;
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.fillText(status, x + padding + dotSize + gap, y + badgeHeight / 2);

  ctx.restore();
}
export function sortByAttention<T extends { status: RhombusCameraSnapshotStatus; score: number | null; locationName: string }>(
  snapshots: T[],
): T[] {
  return sortBy(snapshots, [(s) => STATUS_ATTENTION_ORDER[s.status], (s) => s.score ?? Number.POSITIVE_INFINITY, (s) => s.locationName]);
}

export function sortBySnapshotError<T extends { snapshotError?: string }>(images: T[]): T[] {
  return images.sort((a, b) => Number(!!a.snapshotError) - Number(!!b.snapshotError));
}

export function cameraErrorMessage(image: { cameraName: string; snapshotError?: string }): string {
  switch (image.snapshotError) {
    case SnapshotError.CameraOffline:
      return `${image.cameraName} is offline`;
    case SnapshotError.SnapshotFailed:
      return `${image.cameraName} snapshot failed`;
    default:
      return `${image.cameraName} snapshot unavailable`;
  }
}

export async function embedSnapshotMetadata(
  imageBlob: Blob,
  locationName: string,
  cameraName: string,
  status: RhombusCameraSnapshotStatus,
): Promise<Blob> {
  const bitmap = await createImageBitmap(imageBlob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return imageBlob;

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  drawImageLabel(ctx, canvas.width, canvas.height, cameraName, locationName);
  drawStatusBadge(ctx, canvas.width, status);

  return await canvasToBlob(canvas, imageBlob);
}
